<script setup>
import { ref } from "vue";
import { storeToRefs } from "pinia";
import { useRoute, useRouter } from "vue-router";
import { ChevronDown, Copy, Trash2, LayoutDashboard, Plus, Pencil } from "lucide-vue-next";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
import { useConfirmDelete } from "@/utils/composables/useConfirmDelete";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const route = useRoute();
const router = useRouter();
const store = useResultsV2Store();
const { views, currentView, isDefaultView, editMode } = storeToRefs(store);

const showRenameDialog = ref(false);
const showCreateDialog = ref(false);
const dialogName = ref("");
const targetView = ref(null);
const popoverOpen = ref(false);

function queryForOtherView() {
  const { layout, ...rest } = route.query;
  return rest;
}

function handleSelectView(view) {
  popoverOpen.value = false;
  router.push({
    name: "SurveyResultsV2View",
    params: { viewToken: view.token },
    query: queryForOtherView(),
  });
}

function handleSelectDefault() {
  popoverOpen.value = false;
  router.push({ name: "SurveyResultsV2", query: queryForOtherView() });
}

function openCreateDialog() {
  dialogName.value = "";
  showCreateDialog.value = true;
}

async function handleCreate() {
  if (!dialogName.value.trim()) return;
  const newView = await store.createEmptyView(dialogName.value.trim());
  showCreateDialog.value = false;
  popoverOpen.value = false;
  if (newView) {
    router.push({ name: "SurveyResultsV2View", params: { viewToken: newView.token } });
  }
}

function openRenameDialog(view) {
  targetView.value = view;
  dialogName.value = view?.name || "";
  showRenameDialog.value = true;
}

async function handleRename() {
  if (!dialogName.value.trim() || !targetView.value) return;
  await store.updateView({ name: dialogName.value.trim() }, targetView.value.token);
  showRenameDialog.value = false;
}

async function handleDuplicate(view) {
  if (!view) return;
  const newView = await store.duplicateView(view.token);
  if (newView) {
    router.push({ name: "SurveyResultsV2View", params: { viewToken: newView.token } });
  }
}

const {
  isOpen: showDeleteDialog,
  target: viewToDelete,
  requestDelete: openDeleteDialog,
  confirm: confirmDelete,
} = useConfirmDelete(async (view) => {
  const isCurrentView = currentView.value?.token === view.token;
  await store.deleteView(view.token);
  if (isCurrentView) {
    router.push({ name: "SurveyResultsV2" });
  }
});

const currentLabel = () => {
  if (isDefaultView.value) return "Default dashboard";
  return currentView.value?.name || "Select dashboard";
};
</script>

<template>
  <Popover v-model:open="popoverOpen">
    <PopoverTrigger as-child>
      <Button variant="outline" class="min-w-0 flex-1 justify-start text-13 font-medium text-primary shadow-xs">
        <LayoutDashboard class="mr-2 h-4 w-4 shrink-0" />
        <span class="min-w-0 flex-1 truncate text-left">{{ currentLabel() }}</span>
        <ChevronDown class="ml-2 h-4 w-4 shrink-0" />
      </Button>
    </PopoverTrigger>
    <PopoverContent align="start" class="w-56 p-1">
      <div class="px-2 pt-1.5 pb-1 heading-label">Dashboards</div>

      <!-- Default view -->
      <div
        class="group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-13 hover:bg-muted"
        :class="{ 'bg-muted': isDefaultView }">
        <button
          class="flex-1 text-left truncate min-w-0"
          @click="handleSelectDefault">
          Default dashboard
        </button>
        <button
          v-if="editMode"
          class="shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-foreground"
          @click.stop="handleDuplicate(currentView)">
          <Copy class="h-3 w-3" />
        </button>
      </div>

      <template v-if="views.length">
        <div class="my-1 border-t" />

        <!-- Saved views -->
        <div
          v-for="view in views"
          :key="view.id"
          class="group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-13 hover:bg-muted"
          :class="{ 'bg-muted': currentView?.id === view.id && !isDefaultView }">
          <button
            class="flex-1 text-left truncate min-w-0"
            @click="handleSelectView(view)">
            {{ view.name }}
          </button>
          <template v-if="editMode">
            <button
              class="shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-foreground"
              @click.stop="openRenameDialog(view)">
              <Pencil class="h-3 w-3" />
            </button>
            <button
              class="shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-foreground"
              @click.stop="handleDuplicate(view)">
              <Copy class="h-3 w-3" />
            </button>
            <button
              class="shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive"
              @click.stop="openDeleteDialog(view)">
              <Trash2 class="h-3 w-3" />
            </button>
          </template>
        </div>
      </template>

      <template v-if="editMode">
        <div class="my-1 border-t" />
        <Button
          variant="ghost"
          size="sm"
          class="w-full justify-start font-normal"
          @click="openCreateDialog">
          <Plus />
          New dashboard
        </Button>
      </template>
    </PopoverContent>
  </Popover>

  <!-- Create dialog -->
  <Dialog v-model:open="showCreateDialog">
    <DialogContent>
      <DialogHeader>
        <DialogTitle>New dashboard</DialogTitle>
      </DialogHeader>
      <Input v-model="dialogName" placeholder="Dashboard name..." @keydown.enter="handleCreate" />
      <DialogFooter>
        <Button variant="outline" @click="showCreateDialog = false">Cancel</Button>
        <Button :disabled="!dialogName.trim()" @click="handleCreate">Create</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- Rename dialog -->
  <Dialog v-model:open="showRenameDialog">
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Rename dashboard</DialogTitle>
      </DialogHeader>
      <Input v-model="dialogName" placeholder="Dashboard name..." @keydown.enter="handleRename" />
      <DialogFooter>
        <Button variant="outline" @click="showRenameDialog = false">Cancel</Button>
        <Button :disabled="!dialogName.trim()" @click="handleRename">Rename</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <ConfirmDialog
    v-model:open="showDeleteDialog"
    title="Delete dashboard?"
    confirm-label="Delete"
    @confirm="confirmDelete">
    This will permanently delete "{{ viewToDelete?.name }}". This action cannot be undone.
  </ConfirmDialog>
</template>
